// Highlight-on-arrival (wave-2 N2). When a ref chip / goto button navigates it
// carries { agentFocus: { type, id } } in router state. A destination page calls
// useAgentFocus(type) to get the id to flash once, then the state is cleared so a
// refresh or back-nav doesn't re-flash.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export function useAgentFocus(type: string): string | null {
  const location = useLocation();
  const navigate = useNavigate();
  const focus = (location.state as { agentFocus?: { type: string; id: string } } | null)?.agentFocus;
  const id = focus?.type === type ? focus.id : null;
  const [flash, setFlash] = useState<string | null>(id);

  useEffect(() => {
    if (!id) return;
    setFlash(id);
    // Consume the state so the flash is one-shot.
    navigate(location.pathname + location.search, { replace: true, state: null });
    const t = setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return flash;
}

/**
 * Row-level focus for list pages (the visits pattern, generalised). Resolves the
 * row id to flash from either router state (`agentFocus` with a composite
 * "<parent>/<rowId>" id — the row half is returned) or a query-param deep link
 * (?action=, ?row=, …), plus a scroll-once callback ref for the matching row:
 *
 *   const { flashId, scrollRef } = useRowFocus("action", "action");
 *   ... rows.map(a => <tr ref={a.id === flashId ? scrollRef : undefined}
 *                        className={a.id === flashId ? "agent-focus-flash" : ""}>
 */
export function useRowFocus(type: string, param: string): {
  flashId: string | null;
  scrollRef: (el: HTMLElement | null) => void;
} {
  const [params] = useSearchParams();
  const stateId = useAgentFocus(type);
  const flashId = (stateId ? stateId.split("/").pop() : null) ?? params.get(param);
  const scrolled = useRef<string | null>(null);
  const scrollRef = useCallback(
    (el: HTMLElement | null) => {
      if (!el || !flashId || scrolled.current === flashId) return;
      scrolled.current = flashId;
      el.scrollIntoView({ block: "center" });
    },
    [flashId],
  );
  return { flashId, scrollRef };
}
